import { useState, type ReactNode } from "react"
import { Briefcase, Loader2, User } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type {
  PlacementContextSelectorState,
  PlacementJobOption,
} from "@/hooks/use-placement-context-selection"

type PlacementContextSelectorProps = PlacementContextSelectorState

export function PlacementContextSelector({
  jobSearch,
  setJobSearch,
  candidateSearch,
  setCandidateSearch,
  selectedJobId,
  setSelectedJobId,
  selectedJobCandidateId,
  setSelectedJobCandidateId,
  jobOptions,
  candidateOptions,
  isLoadingJobs,
  isLoadingCandidates,
  hasJobsError,
  hasCandidatesError,
  retryJobs,
  retryCandidates,
}: PlacementContextSelectorProps) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="space-y-2">
        <Label>Job</Label>
        <SearchField
          value={jobSearch}
          placeholder="Search jobs..."
          isLoading={isLoadingJobs}
          emptyMessage="No jobs found."
          onValueChange={(value) => {
            setJobSearch(value)
            if (selectedJobId) {
              setSelectedJobId("")
            }
          }}
          items={jobOptions}
          getKey={(job) => job.id}
          selectedKey={selectedJobId}
          errorMessage={hasJobsError ? "Could not load jobs." : undefined}
          onRetry={retryJobs}
          onSelect={(job) => {
            setSelectedJobId(job.id)
            setJobSearch(formatJobLabel(job))
          }}
          renderItem={(job) => (
            <>
              <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{job.title}</div>
                <div className="truncate text-xs text-muted-foreground">{job.companyName || "Unknown company"}</div>
              </div>
            </>
          )}
        />
      </div>

      <div className="space-y-2">
        <Label>Candidate</Label>
        <SearchField
          value={candidateSearch}
          placeholder={selectedJobId ? "Search candidates in this job..." : "Select a job first"}
          disabled={!selectedJobId}
          isLoading={isLoadingCandidates}
          emptyMessage="No candidates found in this job."
          onValueChange={(value) => {
            setCandidateSearch(value)
            if (selectedJobCandidateId) {
              setSelectedJobCandidateId("")
            }
          }}
          items={candidateOptions}
          getKey={(candidate) => candidate.jobCandidateId}
          selectedKey={selectedJobCandidateId}
          errorMessage={hasCandidatesError ? "Could not load candidates for this job." : undefined}
          onRetry={retryCandidates}
          onSelect={(candidate) => {
            setSelectedJobCandidateId(candidate.jobCandidateId)
            setCandidateSearch(candidate.name)
          }}
          renderItem={(candidate) => (
            <>
              <User className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{candidate.name}</div>
                <div className="truncate text-xs text-muted-foreground">{candidate.stage ?? "Pipeline candidate"}</div>
              </div>
            </>
          )}
        />
      </div>
    </div>
  )
}

type SearchFieldProps<T> = {
  value: string
  placeholder: string
  items: T[]
  selectedKey: string
  isLoading: boolean
  emptyMessage: string
  disabled?: boolean
  errorMessage?: string
  onValueChange: (value: string) => void
  onSelect: (item: T) => void
  onRetry?: () => void
  getKey: (item: T) => string
  renderItem: (item: T) => ReactNode
}

function SearchField<T>({
  value,
  placeholder,
  items,
  selectedKey,
  isLoading,
  emptyMessage,
  disabled,
  errorMessage,
  onValueChange,
  onSelect,
  onRetry,
  getKey,
  renderItem,
}: SearchFieldProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const shouldShowDropdown = isOpen && !disabled

  return (
    <div className="relative">
      <Input
        value={value}
        disabled={disabled}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(event) => {
          onValueChange(event.target.value)
          setIsOpen(true)
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => window.setTimeout(() => setIsOpen(false), 150)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setIsOpen(false)
          }

          if (event.key === "Enter" && items[0]) {
            event.preventDefault()
            onSelect(items[0])
            setIsOpen(false)
          }
        }}
      />

      {shouldShowDropdown ? (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-lg">
          {isLoading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : errorMessage ? (
            <div className="space-y-2 px-3 py-2 text-sm text-muted-foreground">
              <div>{errorMessage}</div>
              {onRetry ? (
                <button
                  type="button"
                  className="text-xs font-medium text-foreground underline underline-offset-4"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={onRetry}
                >
                  Retry
                </button>
              ) : null}
            </div>
          ) : items.length ? (
            <div className="max-h-64 overflow-y-auto py-1">
              {items.map((item) => {
                const key = getKey(item)

                return (
                  <button
                    key={key}
                    type="button"
                    className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-accent"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onSelect(item)
                      setIsOpen(false)
                    }}
                    aria-selected={selectedKey === key}
                  >
                    {renderItem(item)}
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">{emptyMessage}</div>
          )}
        </div>
      ) : null}
    </div>
  )
}

function formatJobLabel(job: PlacementJobOption) {
  return job.companyName ? `${job.title} @ ${job.companyName}` : job.title
}
